import type { ReactNode } from "react";
import { Bug, Megaphone, Search, Sparkles, SquarePen } from "lucide-react";
import type { Conversation } from "../../api/messaging.api";
import type { StudentConversation } from "../../api/student-messaging.api";
import type { MessagingTarget } from "./full-page";
import { StudentMark } from "./StudentThread";
import { STUDENT_KIND_LABEL } from "./student-shared";
import { PresenceText } from "./presence";
import { ConversationAvatar, IconButton, shortWhen } from "./parts";

/** Ligne de Permio AI : une réponse arrivée hors écran compte comme non lue. */
export type AssistantSummary = {
  id: string;
  title: string;
  unread: number;
  updatedAt?: string;
  /** Une réponse est en préparation. */
  busy: boolean;
  /** Ce que Permio a préparé de lui-même et qui attend d'être ouvert ou ignoré. */
  prepared: number;
};

/** Une ligne de la liste unique : conversation de l'équipe, avec un ou des élèves, ou avec Permio AI. */
export type InboxEntry =
  | { source: "team"; conversation: Conversation }
  | { source: "student"; conversation: StudentConversation }
  | { source: "assistant"; conversation: AssistantSummary };

/** Filtres de la liste : tout, ce qui attend, les élèves seuls, l'équipe seule. */
export type InboxFilter = "all" | "unread" | "students" | "team";

export const TEAM_KIND_LABEL: Record<Conversation["type"], string> = {
  DIRECT: "Message direct",
  GROUP: "Groupe privé",
  SCHOOL: "Salon école",
  AGENCY: "Salon agence",
};

const activity = (iso?: string) => (iso ? Date.parse(iso) || 0 : 0);

/**
 * Les deux messageries dans une seule liste, l'activité la plus récente d'abord.
 * Permio AI, quand le compte l'a, reste épinglé en tête.
 */
export function mergeInbox(
  team: Conversation[],
  students: StudentConversation[],
  assistant?: AssistantSummary | null,
): InboxEntry[] {
  const people: InboxEntry[] = [
    ...team.map((conversation) => ({ source: "team" as const, conversation })),
    ...students.map((conversation) => ({
      source: "student" as const,
      conversation,
    })),
    // Tri stable : à date égale ou absente, l'ordre rendu par chaque serveur est gardé.
  ].sort(
    (a, b) =>
      activity(b.conversation.updatedAt) - activity(a.conversation.updatedAt),
  );
  return assistant
    ? [{ source: "assistant", conversation: assistant }, ...people]
    : people;
}

// À voir : des messages non lus, ou ce que Permio a préparé.
const waiting = (entry: InboxEntry) =>
  entry.conversation.unread +
  (entry.source === "assistant" ? entry.conversation.prepared : 0);

const FILTERS: { value: InboxFilter; label: string }[] = [
  { value: "all", label: "Toutes" },
  { value: "unread", label: "Non lues" },
  { value: "students", label: "Élèves" },
  { value: "team", label: "Équipe" },
];

export function Inbox({
  entries,
  current,
  loading,
  unreadConversations,
  sharing,
  canCreate,
  note,
  staleStudents,
  search,
  onSearch,
  filter,
  onFilter,
  onCreate,
  onBroadcast,
  onReport,
  onOpen,
  page = false,
  actions,
}: {
  entries: InboxEntry[];
  /** Conversation ouverte à côté de la liste (pleine page). */
  current?: MessagingTarget | null;
  /** Conversations avec élèves encore en chargement : la liste vide n'est pas encore sûre. */
  loading: boolean;
  unreadConversations: number;
  /** Un objet interne est à partager : seules les conversations de l'équipe sont proposées (ni élèves, ni Permio AI). */
  sharing: boolean;
  canCreate: boolean;
  /** Un mot quand rien ne peut être commencé (compte en lecture seule). */
  note?: string;
  /** Liste des conversations avec élèves non actualisée : nouvel essai. */
  staleStudents?: () => void;
  search: string;
  onSearch: (value: string) => void;
  filter: InboxFilter;
  onFilter: (value: InboxFilter) => void;
  onCreate: () => void;
  /** « Message à tous les élèves », en un clic (secrétariat) ; absent : pas de bouton. */
  onBroadcast?: () => void;
  /** Signaler un problème au service technique Permio : ouvert à tous les comptes. */
  onReport: () => void;
  onOpen: (entry: InboxEntry) => void;
  /** Pleine page : la liste porte le titre de la page. */
  page?: boolean;
  /** Boutons ajoutés à côté de « Nouveau message » (pleine page : réduire en volet). */
  actions?: ReactNode;
}) {
  // Aucune référence interne dans une conversation lue par un élève.
  const offered = sharing
    ? entries.filter((entry) => entry.source === "team")
    : entries;
  // Élèves / Équipe : proposés seulement quand la liste mêle les deux.
  const mixed =
    offered.some((entry) => entry.source === "student") &&
    offered.some((entry) => entry.source === "team");
  const shown: InboxFilter =
    !mixed && (filter === "students" || filter === "team") ? "all" : filter;
  const query = search.trim().toLocaleLowerCase();
  const visible = offered.filter(
    (entry) =>
      (shown === "all" ||
        (shown === "unread" && waiting(entry) > 0) ||
        (shown === "students" && entry.source === "student") ||
        (shown === "team" && entry.source === "team")) &&
      entry.conversation.title.toLocaleLowerCase().includes(query),
  );
  const Title = page ? "h1" : "h2";
  return (
    <div className="messaging-inbox">
      <div className="messaging-inbox-head">
        <div className="messaging-inbox-title">
          <Title>{page ? "Messages" : "Conversations"}</Title>
          <span className="messaging-inbox-count">
            {unreadConversations} non lue(s)
          </span>
        </div>
        <div className="messaging-inbox-actions">
          {canCreate && (
            <IconButton
              icon={SquarePen}
              label="Nouveau message"
              tone="primary"
              onClick={onCreate}
            />
          )}
          {onBroadcast && !sharing && (
            <IconButton
              icon={Megaphone}
              label="Message à tous les élèves"
              onClick={onBroadcast}
            />
          )}
          {actions}
        </div>
      </div>
      {sharing && (
        <p role="status" className="messaging-inbox-notice">
          Choisissez une conversation pour partager l’objet. L’envoi restera à
          confirmer.
          {entries.some((entry) => entry.source === "student") &&
            " Les conversations avec des élèves ne sont pas proposées : aucune référence interne n’y est partagée."}
        </p>
      )}
      <label className="messaging-inbox-search">
        <Search size={16} aria-hidden="true" />
        <input
          type="search"
          aria-label="Rechercher une conversation"
          placeholder="Rechercher une conversation"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </label>
      <div
        className="messaging-filters"
        role="radiogroup"
        aria-label="Afficher les conversations"
      >
        {FILTERS.filter(
          (option) =>
            mixed || (option.value !== "students" && option.value !== "team"),
        ).map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={shown === option.value}
            className="messaging-filter"
            onClick={() => onFilter(option.value)}
          >
            {option.label}
            {option.value === "unread" && unreadConversations > 0 && (
              <span aria-hidden="true">{unreadConversations}</span>
            )}
          </button>
        ))}
      </div>
      {note && <p className="messaging-hint">{note}</p>}
      {staleStudents && (
        <p role="status" className="messaging-hint">
          Conversations avec les élèves non actualisées.{" "}
          <button type="button" onClick={staleStudents}>
            Réessayer
          </button>
        </p>
      )}
      <div className="messaging-list-scroll">
        {!entries.length &&
          (loading ? (
            <p role="status" className="messaging-list-empty">
              Chargement…
            </p>
          ) : (
            <p className="messaging-list-empty">Aucune conversation.</p>
          ))}
        {!!entries.length && !visible.length && (
          <p role="status" className="messaging-list-empty">
            Aucune conversation correspondante.
          </p>
        )}
        <ul className="messaging-list">
          {visible.map((entry) => (
            <li
              key={entry.source + ":" + entry.conversation.id}
              className={
                entry.source === "assistant"
                  ? "messaging-list-assistant"
                  : undefined
              }
            >
              <button
                type="button"
                className={waiting(entry) > 0 ? "is-unread" : undefined}
                aria-current={
                  current?.source === entry.source &&
                  current.id === entry.conversation.id
                    ? "true"
                    : undefined
                }
                onClick={() => onOpen(entry)}
              >
                <Row entry={entry} />
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="messaging-inbox-foot">
        <button
          type="button"
          className="messaging-report-link"
          title="Signaler un problème au service technique Permio"
          onClick={onReport}
        >
          <Bug size={14} aria-hidden="true" />
          Signaler un problème
        </button>
      </div>
    </div>
  );
}

/** Contenu d'une ligne : avatar, nom et date, puis ce qui s'y passe et le nombre à voir. */
function Row({ entry }: { entry: InboxEntry }) {
  const count = waiting(entry);
  const badge = count > 0 && (
    <span
      className="messaging-unread"
      aria-label={
        entry.conversation.unread > 0
          ? `${entry.conversation.unread} non lu(s)`
          : `${count} à examiner`
      }
    >
      {count}
    </span>
  );
  const updatedAt = entry.conversation.updatedAt;
  const when = updatedAt && (
    <time className="messaging-row-time" dateTime={updatedAt}>
      {shortWhen(updatedAt)}
    </time>
  );
  if (entry.source === "assistant") {
    const { busy, prepared } = entry.conversation;
    return (
      <>
        <ConversationAvatar kind="assistant" name="Permio AI" />
        <span className="messaging-list-copy">
          <span className="messaging-row-line">
            <strong>{entry.conversation.title}</strong>
            <span className="messaging-assistant-mark">
              <Sparkles size={11} aria-hidden="true" />
              Assistant
            </span>
            {when}
          </span>
          <span className="messaging-row-line">
            <small className="messaging-list-last">
              {busy
                ? "Réponse en cours…"
                : prepared > 0
                  ? `Permio a préparé ${prepared} action${prepared > 1 ? "s" : ""} à examiner.`
                  : "Posez une question ou demandez une action."}
            </small>
            {badge}
          </span>
        </span>
      </>
    );
  }
  if (entry.source === "team") {
    const c = entry.conversation;
    return (
      <>
        <ConversationAvatar
          kind="team"
          name={c.title}
          staffId={c.type === "DIRECT" ? c.peerId : null}
          presence={c.presence}
        />
        <span className="messaging-list-copy">
          <span className="messaging-row-line">
            <strong>
              {c.title}
              <PresenceText presence={c.presence} />
            </strong>
            {when}
          </span>
          <span className="messaging-row-line">
            <small className="messaging-list-last">
              {TEAM_KIND_LABEL[c.type]}
            </small>
            {badge}
          </span>
        </span>
      </>
    );
  }
  const c = entry.conversation;
  return (
    <>
      <ConversationAvatar
        kind="student"
        name={c.title}
        candidateId={c.kind !== "GROUP" ? c.candidateId : null}
        presence={c.presence}
      />
      <span className="messaging-list-copy">
        <span className="messaging-row-line">
          <strong>
            {c.title}
            <PresenceText presence={c.presence} />
          </strong>
          {when}
        </span>
        <span className="messaging-row-line">
          <StudentMark kind={c.kind} />
          <span className="messaging-sr-only">
            {" "}
            {STUDENT_KIND_LABEL[c.kind]}.{" "}
          </span>
          <small className="messaging-list-last">
            {c.last ? `${c.last.author} : ${c.last.body}` : "Aucun message"}
          </small>
          {badge}
        </span>
      </span>
    </>
  );
}
